import { resolveOpenerTheme } from "@loot-os/api-contract";
import type { LootTheme } from "@loot-os/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useMemo } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { OpenerStyleEditor } from "../components/OpenerStyleEditor";
import { useOpenerStyleWrites } from "../lib/openerStyles";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * Openers → one style: creates (`openers/new`) or edits (`openers/$styleId`) one of the
 * environment's own opener styles, previewed over the storefront theme. A platform style is
 * listed on Openers but is not this environment's to edit. Keyed on the environment, like
 * Shipping, so switching environments drops the draft rather than carrying it across.
 */
export function OpenerStylePage() {
  const { environmentId } = useStorefrontConfigQuery();
  return <OpenerStyleForEnvironment key={environmentId ?? "live"} />;
}

function OpenerStyleForEnvironment() {
  const { tenantId, styleId } = useParams({ strict: false }) as {
    tenantId: string;
    styleId?: string;
  };
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { can } = useTenantRole();
  const { query: config } = useStorefrontConfigQuery();
  const query = useQuery(trpc.openerStyles.list.queryOptions());
  const storedTheme = config.data?.config.theme;
  const hostTheme = useMemo(
    (): LootTheme | undefined => resolveOpenerTheme(storedTheme),
    [storedTheme],
  );
  const toList = () => void navigate({ to: "/tenants/$tenantId/openers", params: { tenantId } });
  const { create, update } = useOpenerStyleWrites("environment", { onSaved: toList });

  if (config.error) return <ErrorAlert message={config.error.message} />;
  if (!can("editor")) return <ErrorAlert message="Only editors can change opener styles." />;
  if (query.error) return <ErrorAlert message={query.error.message} />;
  if (!query.data || !config.data)
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  const style = styleId
    ? query.data.styles.find((s) => s.id === styleId && s.scope === "environment")
    : null;
  if (style === undefined) {
    return <ErrorAlert message="This opener style is not one of this environment's own." />;
  }

  return (
    <OpenerStyleEditor
      key={style?.id ?? "new"}
      style={style}
      hostTheme={hostTheme}
      logoSvg={query.data.logoSvg}
      brandName={query.data.brandName}
      hostLabel="the storefront theme"
      saving={create.isPending || update.isPending}
      onCancel={toList}
      onSave={(values) =>
        style ? update.mutate({ styleId: style.id, ...values }) : create.mutate(values)
      }
    />
  );
}
